import Base from './Base';

export default class Phone extends Base {
    constructor(item) {
        if (!item) item = {};

        super(item);

        if (item.formatted_number) {
            this.formattedNumber = item.formatted_number;
        }
    }

    get color() {
        const palette = [
            '#6372A5',
            '#1D95F2',
            '#10D0AD',
            '#E13466',
            '#FFC422',
            '#FF9423',
            '#A233E1',
        ];

        return palette[this.id % palette.length];
    }

    get isLandline() {
        return Boolean(this.is_landline && this.landline && !this.is_zipwhip);
    }

    get isZipwhip() {
        return this.is_landline && this.landline && this.is_zipwhip;
    }

    get isTollFree() {
        return this.is_toll_free;
    }

    get isAircall() {
        return Boolean(this.is_aircall);
    }

    get isShortCode() {
        return Boolean(this.short_code);
    }

    get isLocal() {
        return !this.isLandline && !this.isTollFree && !this.isAircall && !this.isShortCode;
    }

    get isMobile() {
        return this.is_mobile;
    }

    get statusLandLine() {
        return this.landline && this.landline.status;
    }

    get statusTwilioProcessing() {
        return this.statusLandLine === 'twilio-processing'; // 2 - status delete - false
    }

    get statusReceived() {
        return this.statusLandLine === 'received'; // 3 - status delete - true
    }

    get statusPendingVerification() {
        return this.statusLandLine === 'pending-verification'; // 3 - status delete - true
    }

    get statusVerified() {
        return this.statusLandLine === 'verified'; // 4 - status delete - true
    }

    get statusPendingLoa() {
        return this.statusLandLine === 'pending-loa'; // 4 - 30sec fetch - status delete - false
    }

    get statusCarrierProcessing() {
        return this.statusLandLine === 'carrier-processing'; // 4 - success - status delete - false
    }

    get statusTesting() {
        return this.statusLandLine === 'testing'; // 4 - status delete - false
    }

    get statusCompleted() {
        return this.statusLandLine === 'completed'; // 5 - status delete - false
    }

    get statusMigration() {
        return Boolean(this.numberable); // 7 - (Zipwhip)
    }

    get statusActionRequired() {
        return this.statusLandLine === 'action-required'; // - status delete - false
    }

    get statusFailed() {
        return this.statusLandLine === 'failed'; // status delete - false
    }

    get statusCanceled() {
        return this.statusLandLine === 'canceled';
    }

    get disabledStatus() {
        return (
            this.statusTwilioProcessing ||
            this.statusPendingLoa ||
            this.statusCarrierProcessing ||
            this.statusTesting ||
            this.statusCompleted ||
            this.statusActionRequired ||
            this.statusFailed
        );
    }

    get hasVerify() {
        return (
            this.statusReceived ||
            this.statusPendingVerification
        );
    }

    get hasVerified() {
        return (
            this.statusVerified
        );
    }

    get hasLOA() {
        return (
            this.statusPendingLoa ||
            this.statusCarrierProcessing ||
            this.statusTesting
        );
    }

    get hasCompleted() {
        if (this.is_zipwhip) {
            return (
                this.statusCompleted &&
                !this.statusMigration
            );
        }

        return this.statusCompleted;
    }

    get hasMigration() {
        return (
            this.statusMigration &&
            this.statusCompleted
        );
    }

    get hasVerificationAttempts() {
        return this.landline && this.landline.verification_attempts >= 3;
    }

    get hasPersonalCompleted() {
        return (
            this.statusCompleted &&
            this.landline &&
            this.landline.completed === 1
        );
    }

    get canVerify() {
        return this.landline && this.landline.can_verify;
    }

    get hasIncomplete() {
        return (
            this.statusTwilioProcessing ||
            this.statusReceived ||
            this.statusPendingVerification ||
            this.statusVerified ||
            this.statusCanceled
        );
    }

    get hasProcessing() {
        return (
            this.statusPendingLoa
        );
    }

    get hasDeleted() {
        if (!this.landline) return true;

        return Boolean(this.landline.completed);
    }

    get inbox() {
        if (!this.numberable) return {};

        return this.numberable;
    }

    get inboxId() {
        if (typeof this.inbox.current_team_type_id === 'number') {
            return this.inbox.current_team_type_id;
        }

        return this.inbox.id;
    }

    get isDefaultNumberableAvatar() {
        if (!this.numberable || !this.numberable.photo_url) return false;

        return this.numberable.photo_url.includes(`${window.location.hostname}/images/app/default-avatar.png`);
    }

    get hasAircall() {
        return this.number_vendor.key === 'aircall-api';
    }

    get hasNumberVendorInteliquent() {
        return this.number_vendor.key === 'inteliquent';
    }

    get nameOrEmail() {
        if (!this.numberable) {
            return '';
        }
        if (this.hasAircall) {
            if (!this.numberable.full_name) {
                return 'Invitation pending';
            }

            return this.numberable.full_name
                ? this.numberable.full_name
                : this.numberable.email;
        }

        return this.numberable.full_name
            ? this.numberable.full_name
            : this.numberable.email;
    }

    get callForwardingIsActive() {
        return this.call_forwarding && this.call_forwarding.is_active;
    }

    get callForwardingExtension() {
        return this.call_forwarding.extension
            ? 'ext: ' + this.call_forwarding.extension
            : '';
    }

    get labelType() {
        if (this.isMobile) {
            return 'Mobile';
        }

        if (this.isLandline) {
            return 'Landline';
        }

        if (this.isZipwhip) {
            return 'ZipWhip';
        }

        if (this.isTollFree) {
            return 'Toll-Free';
        }

        if (this.isAircall) {
            return 'Aircall';
        }

        if (this.isShortCode) {
            return 'Short Code';
        }

        return 'Local';
    }

    get labelVerifiedStatus() {
        if (!this.isTollFree) return '';

        return this.verified_status;
    }

    get statusDeclined() {
        return this.verified_status === 'Denied';
    }

    get statusUnverified() {
        return this.verified_status === 'Unverified';
    }

    get statusUnderVerification() {
        return this.verified_status === 'Under Review';
    }

    get statusTollFreeVerified() {
        return this.verified_status === 'Verified';
    }

    get statusBlocked() {
        return this.verified_status === 'Blocked';
    }

    get statusInternalReview() {
        return this.verified_status === 'Internal Review';
    }

    get isSendToTollFreeVerification() {
        return Boolean(this.toll_free_verification_sid);
    }

    get fullNameNumberable() {
        if (this.isNumberableTypeUsersUser) {
            return (this?.numberable?.full_name) || '-';
        }

        if (this.numberable_type === 'App\\Teams\\Team') {
            return (this?.numberable?.name) || 'Unnamed Inbox';
        }

        if (this.numberable_type === 'App\\Numbers\\Shortcode') {
            return (this?.numberable?.name) || 'Shortcode';
        }

        return '';
    }

    get isNumberableTypeUsersUser() {
        return this.numberable_type === 'App\\Users\\User';
    }

    get isNumberableTypeTeamsTeam() {
        return this.numberable_type === 'App\\Teams\\Team';
    }

    get isNumberableTypeNumbersShortcode() {
        return this.numberable_type === 'App\\Numbers\\Shortcode';
    }

    get photoUrl() {
        return this.numberable.photo_url;
    }
}
